/* ==========================================================================
   Pet Portrait PDP — <pet-portrait-gallery>: main image + synced thumbnail
   strip via the theme's Swiper. Self-managing, theme-editor safe.
   ========================================================================== */
(function () {
  'use strict';

  class PetPortraitGallery extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      // Deferred scripts load Swiper first; guard anyway. Retry next frame, but
      // cap attempts so a blocked Swiper can't spin an unbounded rAF loop (~120 ≈ 2s).
      if (typeof window.Swiper === 'undefined') {
        this._swiperRetries = (this._swiperRetries || 0) + 1;
        if (this._swiperRetries > 120) return;
        this._retry = window.requestAnimationFrame(this.connectedCallback.bind(this));
        return;
      }

      this._initialized = true;
      this.init();
    }

    disconnectedCallback() {
      if (this._retry) window.cancelAnimationFrame(this._retry);
      this.destroy();
    }

    init() {
      this.mainEl = this.querySelector('.pp-gallery__main');
      this.thumbsEl = this.querySelector('.pp-gallery__thumbs');
      if (!this.mainEl) return;

      var perView = parseInt(this.dataset.thumbsPerView, 10) || 6;
      var perViewMobile = parseInt(this.dataset.thumbsPerViewMobile, 10) || 5;
      var gap = parseInt(this.dataset.thumbsGap, 10) || 10;
      var loop = this.dataset.loop === 'true';
      var arrows = this.dataset.arrows === 'true';
      this.loop = loop;

      // Capture the original media order BEFORE Swiper clones slides (loop mode),
      // so selectMedia can map a variant's featured media id to a slide index.
      var originalSlides = this.mainEl.querySelectorAll('.swiper-wrapper > .swiper-slide');
      this.mediaIds = Array.prototype.map.call(originalSlides, function (s) {
        return s.getAttribute('data-media-id') || '';
      });

      if (this.thumbsEl) {
        // Mobile-first: base is the mobile count; the 750px breakpoint raises it
        // for desktop. Swiper re-reads on resize, so no re-init on rotate/preview.
        this.thumbsSwiper = new window.Swiper(this.thumbsEl, {
          slidesPerView: perViewMobile,
          spaceBetween: gap,
          freeMode: true,
          watchSlidesProgress: true,
          breakpoints: {
            750: { slidesPerView: perView }
          }
        });
      }

      var options = {
        loop: loop,
        // Mobile: let a sliver of the next slide peek so it reads as swipeable.
        // Desktop (>=750px) shows one full-width slide; thumbs do the browsing.
        spaceBetween: 10,
        slidesPerView: 1.12,
        watchOverflow: true,
        breakpoints: {
          750: { slidesPerView: 1, spaceBetween: 0 }
        }
      };

      if (arrows) {
        options.navigation = {
          nextEl: this.querySelector('.pp-gallery__nav--next'),
          prevEl: this.querySelector('.pp-gallery__nav--prev')
        };
      }

      if (this.thumbsSwiper) {
        options.thumbs = { swiper: this.thumbsSwiper };
      }

      this.mainSwiper = new window.Swiper(this.mainEl, options);
    }

    /* Public: move the gallery to the slide showing `mediaId`. */
    selectMedia(mediaId) {
      if (!mediaId || !this.mainSwiper || !this.mediaIds) return;
      var idx = this.mediaIds.indexOf(String(mediaId));
      if (idx < 0) return;
      if (this.loop && typeof this.mainSwiper.slideToLoop === 'function') {
        this.mainSwiper.slideToLoop(idx);
      } else {
        this.mainSwiper.slideTo(idx);
      }
    }

    destroy() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
      this._initialized = false;
    }
  }

  if (!window.customElements.get('pet-portrait-gallery')) {
    window.customElements.define('pet-portrait-gallery', PetPortraitGallery);
  }

  /* ==========================================================================
     <pet-portrait-configurator> — variant controller. Reads variant JSON; on
     option change syncs the selected name, price block and gallery image.
     ========================================================================== */
  class PetPortraitConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.section = this.closest('.pp-pdp') || document;
      this.currency = this.dataset.currency || 'USD';

      var dataEl = this.querySelector('[data-pp-variants]');
      try {
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      var selId = parseInt(this.dataset.selectedVariant, 10);
      this.current =
        this.findById(selId) ||
        this.firstAvailable() ||
        this.variants[0] ||
        null;
      this.baseline = this.current ? this.current.o.slice() : [];

      // Step navigation state
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-pp-panel]'));
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-pp-tab]'));
      this.tabsEl = this.querySelector('.pp-config__tabs');
      this.total = parseInt(this.dataset.totalSteps, 10) || this.panels.length;
      this.bar = this.querySelector('[data-pp-progress-bar]');
      this.stepCurrentEl = this.querySelector('[data-pp-step-current]');
      this.hintEl = this.querySelector('[data-pp-progress-hint]');
      // Progress-hint copy comes from locale data-attributes (translatable);
      // "{n}" is the remaining-step count. Fallbacks cover a missing attribute.
      this.hintFinal = this.getAttribute('data-pp-hint-final') || 'Final step';
      this.hintTogoOne = this.getAttribute('data-pp-hint-togo-one') || '[n] step to go';
      this.hintTogoOther = this.getAttribute('data-pp-hint-togo-other') || '[n] steps to go';
      this.nav = this.querySelector('.pp-config__nav');
      this.backBtn = this.nav && this.nav.querySelector('[data-pp-back]');
      this.nextBtn = this.nav && this.nav.querySelector('[data-pp-next]');
      this.step = 0;

      // Running-total summary + free-shipping progress (base + sleeve scale with qty).
      this.baseEl = this.querySelector('[data-pp-base]');
      this.totalEl = this.querySelector('[data-pp-total]');
      this.sleeveSummaryRow = this.querySelector('[data-pp-summary-sleeve]');
      this.sleeveSummaryValue = this.querySelector('[data-pp-sleeve-value]');
      this.ftSummaryRow = this.querySelector('[data-pp-summary-fasttrack]');
      // Compare-at "was" total + saving (present only when the setting is on).
      this.compareHolder = this.querySelector('[data-pp-compare-holder]');
      this.compareMoney = this.querySelector('[data-pp-compare-money]');
      this.saveHolder = this.querySelector('[data-pp-save-holder]');
      this.saveMoney = this.querySelector('[data-pp-save-money]');
      this.freeshipEl = this.querySelector('[data-pp-freeship]');

      // "Make another" support: confirmation element, the default ATC label, and a
      // snapshot of the starting variant + checked inputs for the reset.
      this.addedEl = this.querySelector('[data-pp-added]');
      var atcTextEl = this.querySelector('[data-pp-atc-text]');
      this._atcDefaultLabel = atcTextEl ? atcTextEl.textContent : null;
      this._initialVariantId = this.current ? this.current.id : null;
      this._initialCheckedInputs = Array.prototype.slice.call(
        this.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')
      );

      this._onChange = this.onChange.bind(this);
      this._onClick = this.onClick.bind(this);
      this._onInput = this.onInput.bind(this);
      this._onKey = this.onKey.bind(this);
      this._onSubmit = this.onSubmit.bind(this);
      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('input', this._onInput);
      this.addEventListener('submit', this._onSubmit);
      document.addEventListener('keydown', this._onKey);
      // Fast Track (sibling block) flips independently — re-total when it changes.
      this._onFtChange = this.recalcTotal.bind(this);
      document.addEventListener('fasttrack:change', this._onFtChange);

      // Grey out any option value that can't reach an available variant.
      this.refreshAvailability();

      // Normalise the initial view (Liquid already rendered step 1 as active).
      this.showStep(false);
      this.recalcTotal();

      // Start hydrating the photo uploader in the background so it's ready by
      // the time the user reaches the Upload step.
      this.ensureUploader();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('input', this._onInput);
      this.removeEventListener('submit', this._onSubmit);
      document.removeEventListener('keydown', this._onKey);
      if (this._onFtChange) document.removeEventListener('fasttrack:change', this._onFtChange);
      if (this._uploadObserver) this._uploadObserver.disconnect();
      if (this._uploadPoll) clearInterval(this._uploadPoll);
      this._initialized = false;
    }

    onKey(e) {
      // Enter/Space on the role="button" dropzone must open the uploader like a
      // click — a <div role="button"> doesn't synthesise a click on key press.
      if (
        e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' ||
        e.keyCode === 13 || e.keyCode === 32
      ) {
        var drop = e.target && e.target.closest && e.target.closest('[data-pp-drop]');
        if (drop) {
          e.preventDefault();
          this.openUploader();
          return;
        }
      }

      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      var open = this.querySelector('.pp-modal.is-open');
      if (open) this.closeOverlay(open);
    }

    onInput(e) {
      if (!e.target.matches) return;

      // Typing the sleeve name clears the "enter a name" error as they go.
      if (e.target.matches('[data-pp-sleeve-name]')) {
        var panel = e.target.closest('[data-pp-panel]');
        if (panel && e.target.value.trim()) this.clearError(panel);
        return;
      }

      if (!e.target.matches('[data-pp-name-input]')) return;
      var field = e.target.closest('.pp-field');
      var count = field && field.querySelector('[data-pp-count]');
      if (count) count.textContent = e.target.value.length;
    }

    onClick(e) {
      // Safari 14.1–15.3 omits event.submitter; record the clicked ATC button
      // here (click fires before submit) as a fallback.
      var atcClicked = e.target.closest && e.target.closest('[data-pp-atc]');
      if (atcClicked) this._lastSubmitter = atcClicked;
      if (e.target.closest('[data-pp-size-chart]')) {
        e.preventDefault();
        this.openOverlay(this.querySelector('[data-pp-size-chart-modal]'));
        return;
      }
      if (e.target.closest('[data-pp-photo-guide]')) {
        e.preventDefault();
        this.openOverlay(this.querySelector('[data-pp-modal]'));
        return;
      }
      if (e.target.closest('[data-pp-modal-close]')) {
        this.closeOverlay(e.target.closest('.pp-modal'));
        return;
      }
      // Add-to-cart-without-photo popup actions.
      if (e.target.closest('[data-pp-upload-now]')) {
        // Abandon the auto-submit intent; the shopper will click an ATC again.
        this._pendingSubmitter = null;
        this.closeOverlay(this.querySelector('[data-pp-upload-confirm]'));
        this.openUploader();
        return;
      }
      if (e.target.closest('[data-pp-upload-later]')) {
        this._skipPhoto = true;
        this.closeOverlay(this.querySelector('[data-pp-upload-confirm]'));
        this.submitForm();
        return;
      }
      // Let the real UploadKit button (and its rendered UI) behave normally;
      // don't re-trigger from our wrapper when the click originates inside it.
      if (e.target.closest('[data-pp-uploadkit]')) return;
      if (e.target.closest('[data-pp-upload-change]') || e.target.closest('[data-pp-drop]')) {
        this.openUploader();
        return;
      }
      var qtyBtn = e.target.closest('[data-pp-qty-step]');
      if (qtyBtn) {
        this.stepQuantity(parseInt(qtyBtn.getAttribute('data-pp-qty-step'), 10) || 0);
        return;
      }
      if (e.target.closest('[data-pp-next]')) {
        this.go(this.step + 1);
        return;
      }
      if (e.target.closest('[data-pp-back]')) {
        this.go(this.step - 1);
        return;
      }
      var tab = e.target.closest('[data-pp-tab]');
      if (tab) {
        var i = parseInt(tab.getAttribute('data-step-index'), 10);
        if (!isNaN(i)) this.go(i);
      }
    }

    go(i) {
      if (i < 0 || i >= this.total || i === this.step) return;
      // Moving forward: every required step before the target must be satisfied.
      // Moving back is always allowed.
      if (i > this.step) {
        var invalid = this.firstInvalidBefore(i);
        if (invalid !== -1) {
          this.step = invalid;
          this.showStep(true);
          this.showError(this.panels[invalid], this.stepError(invalid));
          return;
        }
      }
      this.step = i;
      this.showStep(true);
    }

    /* --- Required-step validation --- */

    // Is the step at `idx` satisfied? Optional steps are always valid; required
    // steps (variant options + placement) need a checked radio.
    stepValid(idx) {
      return this.stepError(idx) === null;
    }

    // Null when the step is satisfied, otherwise the message to show on it.
    stepError(idx) {
      var panel = this.panels[idx];
      if (!panel) return null;

      if (
        panel.getAttribute('data-pp-required') === 'true' &&
        !panel.querySelector('input[type="radio"]:checked')
      ) {
        return this.dataset.ppValidationMsg || 'Please make a selection to continue.';
      }

      // The sleeve add-on is optional, but once on it must carry a name — a paid
      // blank embroidery is a support ticket. "No Icon" is valid, so not checked.
      var toggle = panel.querySelector('[data-pp-sleeve-toggle]');
      if (toggle && toggle.checked) {
        var nameEl = panel.querySelector('[data-pp-sleeve-name]');
        if (!nameEl || !nameEl.value.trim()) {
          return this.dataset.ppSleeveNameMsg || 'Please enter the name for the sleeve.';
        }
      }

      return null;
    }

    // Index of the first unsatisfied required step in [0, target), or -1.
    firstInvalidBefore(target) {
      for (var idx = 0; idx < target; idx++) {
        if (!this.stepValid(idx)) return idx;
      }
      return -1;
    }

    showError(panel, message) {
      if (!panel) return;
      panel.classList.add('has-error');
      var err = panel.querySelector('[data-pp-error]');
      if (!err) {
        err = document.createElement('p');
        err.className = 'pp-panel__error';
        err.setAttribute('data-pp-error', '');
        panel.appendChild(err);
      }
      err.textContent =
        message || this.dataset.ppValidationMsg || 'Please make a selection to continue.';
      err.hidden = false;
    }

    clearError(panel) {
      if (!panel) return;
      panel.classList.remove('has-error');
      var err = panel.querySelector('[data-pp-error]');
      if (err) err.hidden = true;
    }

    /* --- Add to cart --- */

    onSubmit(e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('.pp-config__form')) return;

      // Enforce required steps even if the form is submitted directly.
      var invalid = this.firstInvalidBefore(this.total);
      if (invalid !== -1) {
        e.preventDefault();
        this.step = invalid;
        this.showStep(true);
        this.showError(this.panels[invalid], this.stepError(invalid));
        return;
      }

      // Prefer the freshly-clicked button; Safari omits e.submitter and
      // _pendingSubmitter only carries the "send later" popup re-submit.
      var submitter = e.submitter || this._lastSubmitter || this._pendingSubmitter || null;
      this._pendingSubmitter = submitter;

      // Photo is optional — if none uploaded, offer to do it now or later.
      if (!this._hasPhoto && !this._skipPhoto) {
        e.preventDefault();
        this.openOverlay(this.querySelector('[data-pp-upload-confirm]'));
        return;
      }

      // Everything passed — add via AJAX and open the theme's cart drawer
      // instead of letting the browser navigate to /cart.
      e.preventDefault();
      this._pendingSubmitter = null;
      this._lastSubmitter = null;
      var makeAnother = !!(submitter && submitter.hasAttribute && submitter.hasAttribute('data-pp-atc-make-another'));
      this.addToCart(form, makeAnother, submitter);
    }

    /* --- Line-item properties --- */

    // Fill the hidden property inputs from the current selections. Called right
    // before FormData is built so it always reflects the final state.
    syncProperties() {
      var placement = this.querySelector('input[name="pp-placement"]:checked');
      this.setProp('placement', placement && placement.value);

      // Personalisation is opt-in and the text is the point — font/colour have
      // checked defaults, so sending them without text would tag every order.
      var input = this.querySelector('[data-pp-name-input]');
      var text = input && input.value ? input.value.trim() : '';
      var font = this.querySelector('input[name="pp-font"]:checked');
      var colour = this.querySelector('input[name="pp-font-colour"]:checked');
      this.setProp('text', text);
      this.setProp('font', text ? font && font.value : '');
      this.setProp('colour', text ? colour && colour.value : '');

      // Always sent — fulfilment needs to know an order is awaiting a photo.
      this.setProp('photo', this._hasPhoto ? 'Uploaded' : 'To email later');
    }

    // Empty value => disabled => excluded from FormData entirely.
    setProp(key, value) {
      var el = this.querySelector('[data-pp-prop="' + key + '"]');
      if (!el) return;
      if (value) {
        el.value = value;
        el.disabled = false;
      } else {
        el.value = '';
        el.disabled = true;
      }
    }

    /* --- Sleeve embroidery add-on --- */

    // The ticked sleeve option, or null. Only rendered when the sleeve metafield
    // resolves to a buyable variant, so a missing id means no card should show.
    sleeveSelection() {
      var toggle = this.querySelector('[data-pp-sleeve-toggle]');
      if (!toggle || !toggle.checked) return null;

      var variantId = parseInt(toggle.getAttribute('data-pp-sleeve-variant'), 10);
      if (!variantId) return null;

      var nameEl = this.querySelector('[data-pp-sleeve-name]');
      var iconEl = this.querySelector('input[name="pp-sleeve-icon"]:checked');
      return {
        variantId: variantId,
        name: nameEl && nameEl.value ? nameEl.value.trim() : '',
        icon: iconEl ? iconEl.value : 'No Icon'
      };
    }

    // Keep the "+$15.00" labels in step with quantity, so the price the customer
    // agrees to on the checkbox is the price that lands in the cart.
    updateSleevePrice() {
      var toggle = this.querySelector('[data-pp-sleeve-toggle]');
      if (!toggle) return;
      var unit = parseInt(toggle.getAttribute('data-pp-sleeve-price'), 10);
      if (!unit) return;

      var text = '+' + this.money(unit * this.quantity());
      var targets = this.querySelectorAll('[data-pp-sleeve-badge], [data-pp-sleeve-price-label]');
      Array.prototype.forEach.call(targets, function (el) {
        el.textContent = text;
      });
    }

    quantity() {
      var input = this.querySelector('[data-pp-qty-input]');
      return Math.max(1, parseInt(input && input.value, 10) || 1);
    }

    /* --- Running-total summary --- */

    // base + sleeve + Fast Track = live total; the sleeve/FT rows follow their
    // toggles. Also builds the compare-at "was" total for the saving badge.
    recalcTotal() {
      if (!this.totalEl) return;
      var qty = this.quantity();
      var unitPrice = this.current ? this.current.p : 0;
      // "was" line = the line's compare-at when it's higher than its price, else the price.
      var unitWas = (this.current && this.current.c && this.current.c > unitPrice) ? this.current.c : unitPrice;
      var base = unitPrice * qty;
      var total = base;
      var wasCents = unitWas * qty;

      var toggle = this.querySelector('[data-pp-sleeve-toggle]');
      var sleeveOn = !!(toggle && toggle.checked);
      if (sleeveOn) {
        var unit = parseInt(toggle.getAttribute('data-pp-sleeve-price'), 10) || 0;
        var unitCompare = parseInt(toggle.getAttribute('data-pp-sleeve-compare'), 10) || 0;
        total += unit * qty;
        wasCents += (unitCompare > unit ? unitCompare : unit) * qty;
        if (this.sleeveSummaryValue) this.sleeveSummaryValue.textContent = '+' + this.money(unit * qty);
      }
      if (this.sleeveSummaryRow) this.sleeveSummaryRow.hidden = !sleeveOn;

      // Fast Track lives in the sibling next-batch block (assets/fast-track.js).
      var ftOn = !!(window.FastTrack && window.FastTrack.isOn(this.section));
      if (ftOn) {
        var ftp = window.FastTrack.priceCents(this.section);
        total += ftp;
        wasCents += ftp; // no compare-at on Fast Track — contributes its price to both
      }
      if (this.ftSummaryRow) this.ftSummaryRow.hidden = !ftOn;

      if (this.baseEl) this.baseEl.textContent = this.money(base);
      this.totalEl.textContent = this.money(total);
      this.updateWasPrice(total, wasCents);
      this.updateFreeship(total);
    }

    // Compare-at "was" total + saving beside the Total — only when the setting is
    // on (the elements exist) and there is an actual discount (was > now).
    updateWasPrice(subCents, wasCents) {
      if (!this.compareHolder) return;
      var hasDiscount = wasCents > subCents;
      this.compareHolder.hidden = !hasDiscount;
      if (this.saveHolder) this.saveHolder.hidden = !hasDiscount;
      if (!hasDiscount) return;
      if (this.compareMoney) this.compareMoney.textContent = this.money(wasCents);
      if (this.saveMoney) this.saveMoney.textContent = this.money(wasCents - subCents);
    }

    /* --- Free-shipping progress --- */

    // Render the away/unlocked message from total vs the store threshold; hidden
    // when no threshold is set (window.__themeFreeShippingThresholdCents).
    updateFreeship(totalCents) {
      var el = this.freeshipEl;
      if (!el) return;
      var threshold = parseInt(window.__themeFreeShippingThresholdCents, 10);
      if (isNaN(threshold) || threshold <= 0) { el.hidden = true; return; }
      el.hidden = false;

      var remaining = threshold - totalCents;
      var tmpl, amountHtml;
      if (remaining > 0) {
        tmpl = el.getAttribute('data-pp-freeship-away') ||
          "You're only [amount] away from **FREE SHIPPING**";
        amountHtml = '<span class="pp-config__freeship-amount">' +
          this.escapeHtml(this.money(remaining)) + '</span>';
      } else {
        tmpl = el.getAttribute('data-pp-freeship-unlocked') ||
          "Congrats! You've earned **FREE SHIPPING**";
        amountHtml = '';
      }
      el.innerHTML = this.renderFreeshipTemplate(tmpl, amountHtml);
    }

    // Escape the merchant template, then apply **bold** and the [amount]/{{amount}} token.
    renderFreeshipTemplate(tmpl, amountHtml) {
      var text = this.escapeHtml(String(tmpl));
      text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      text = text.replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, amountHtml);
      return text;
    }

    escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return {
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c];
      });
    }

    /* --- Add to cart --- */

    // Harvest every enabled properties[…] input: our hidden inputs plus the ones
    // UploadKit injects, so property names stay declared in Liquid, not here.
    collectProperties(form) {
      var props = {};
      var inputs = form.querySelectorAll('[name^="properties["]');
      Array.prototype.forEach.call(inputs, function (el) {
        if (el.disabled) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        var match = el.name.match(/^properties\[(.+)\]$/);
        if (!match || !el.value) return;
        props[match[1]] = el.value;
      });
      return props;
    }

    buildCartItems(form) {
      var idInput = form.querySelector('[data-pp-id]');
      var quantity = this.quantity();

      // Shared stamp linking the add-on line back to its parent. Matches the
      // convention the theme already uses for gift-message add-ons.
      var stamp = String(Date.now());

      var props = this.collectProperties(form);
      props.__time = stamp;

      var items = [
        {
          id: parseInt(idInput && idInput.value, 10),
          quantity: quantity,
          properties: props
        }
      ];

      var sleeve = this.sleeveSelection();
      if (sleeve) {
        items.push({
          id: sleeve.variantId,
          // Scales with the parent: 2 sweatshirts means 2 sleeves charged.
          quantity: quantity,
          properties: {
            'Name on the Sleeve': sleeve.name,
            Icon: sleeve.icon,
            __linked_time: stamp,
            __sleeve_addon: 'true'
          }
        });
      }

      // Fast Track add-on line (logic in assets/fast-track.js).
      var ftLine = window.FastTrack && window.FastTrack.lineItem(this.section, stamp);
      if (ftLine) items.push(ftLine);

      return items;
    }

    addToCart(form, makeAnother, btn) {
      if (this._adding) return;
      this._adding = true;
      this.setAtcLoading(true, btn);
      this.clearAtcError();
      this.hideAdded();
      this.syncProperties();

      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        // One request with both lines — the add-on can't succeed without its parent.
        body: JSON.stringify({ items: this.buildCartItems(form) })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            // Shopify returns 4xx with { description } for sold-out / invalid lines.
            if (!res.ok) throw new Error(data.description || data.message || '');
            return data;
          });
        })
        .then(function () {
          if (makeAnother) {
            // Stay put: refresh the cart, confirm, reset for another piece.
            self.refreshCart();
            self.showAdded();
            self.resetConfigurator();
            if (typeof self.scrollIntoView === 'function') {
              self.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } else {
            self.openCartDrawer();
          }
        })
        .catch(function (err) {
          self.showAtcError(err && err.message);
        })
        .then(function () {
          self._adding = false;
          self.setAtcLoading(false, btn);
        });
    }

    // Refresh the drawer + header count via the theme's reBuildCart (no open).
    refreshCart() {
      if (typeof window.reBuildCart === 'function') {
        try { window.reBuildCart(); return; } catch (e) { /* fall through */ }
      }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count;
          Array.prototype.forEach.call(document.querySelectorAll('.cart_trigger'), function (n) {
            n.setAttribute('cart_item_count', count);
            n.classList.toggle('show_bubble', count > 0);
          });
        })
        .catch(function () { /* non-fatal */ });
    }

    showAdded() {
      if (!this.addedEl) return;
      this.addedEl.textContent = this.getAttribute('data-pp-added-label') || 'Added to cart ✓';
      this.addedEl.hidden = false;
      var self = this;
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(function () { self.hideAdded(); }, 4000);
    }

    hideAdded() {
      if (this.addedEl) this.addedEl.hidden = true;
    }

    // Reset every field to first-render defaults for a fresh build ("Make another").
    resetConfigurator() {
      // Restore radios/checkboxes to the initial snapshot.
      var all = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      Array.prototype.forEach.call(all, function (el) { el.checked = false; });
      (this._initialCheckedInputs || []).forEach(function (el) { el.checked = true; });

      // Re-apply the starting variant (re-syncs option radios, names, price, gallery).
      var start = this.findById(this._initialVariantId) || this.firstAvailable() || this.variants[0];
      if (start) this.apply(start);

      // Clear the personalisation text + its counter.
      var nameInput = this.querySelector('[data-pp-name-input]');
      if (nameInput) {
        nameInput.value = '';
        var field = nameInput.closest('.pp-field');
        var count = field && field.querySelector('[data-pp-count]');
        if (count) count.textContent = '0';
      }

      // Clear the sleeve name; reset quantity to 1; re-price the sleeve.
      var sleeveName = this.querySelector('[data-pp-sleeve-name]');
      if (sleeveName) sleeveName.value = '';
      var qty = this.querySelector('[data-pp-qty-input]');
      if (qty) qty.value = qty.min || 1;
      this.updateSleevePrice();

      // Fast Track lives in a sibling block — switch it off first, then re-total
      // so its price and row are cleared from the summary too.
      if (window.FastTrack) window.FastTrack.reset(this.section);
      this.recalcTotal();

      // Photo + errors, then back to step 1.
      this.resetPhotos();
      var self = this;
      this.panels.forEach(function (p) { self.clearError(p); });
      this.refreshAvailability();
      this.step = 0;
      this.showStep(false);
      // Roll the tab strip back to the first tab (showStep(false) leaves it parked).
      if (this.tabsEl) {
        if (typeof this.tabsEl.scrollTo === 'function') this.tabsEl.scrollTo({ left: 0, behavior: 'auto' });
        else this.tabsEl.scrollLeft = 0;
      }
    }

    // Clear the app's saved upload data + preview so the photo state resets.
    resetPhotos() {
      this._skipPhoto = false;
      this._hasPhoto = false;
      Array.prototype.forEach.call(this.querySelectorAll('[name^="properties["]'), function (el) {
        if (el.type === 'radio' || el.type === 'checkbox') return;
        if (el.hasAttribute('data-pp-prop')) return;
        el.value = '';
      });
      var prompt = this.querySelector('[data-pp-upload-prompt]');
      var preview = this.querySelector('[data-pp-preview]');
      if (preview) preview.hidden = true;
      if (prompt) prompt.hidden = false;
      var drop = this.querySelector('[data-pp-drop]');
      if (drop) drop.classList.remove('has-photo');
      var atcText = this.querySelector('[data-pp-atc-text]');
      if (atcText && this._atcDefaultLabel != null) atcText.textContent = this._atcDefaultLabel;
    }

    // Defer to the theme's global drawer (matt_js.liquid / cart-drawer.js):
    // reBuildCart() re-renders it with the new line, then open_cart_drawer() opens.
    openCartDrawer() {
      if (typeof window.reBuildCart === 'function') window.reBuildCart();
      if (typeof window.open_cart_drawer === 'function') {
        window.open_cart_drawer();
        return;
      }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'cart';
    }

    setAtcLoading(on, btn) {
      // Disable both Add-to-cart buttons; the spinner shows only on the clicked one.
      var buttons = this.querySelectorAll('[data-pp-atc]');
      var active = btn || buttons[0];
      Array.prototype.forEach.call(buttons, function (b) {
        b.disabled = !!on;
        b.setAttribute('aria-busy', on ? 'true' : 'false');
        var isActive = b === active;
        b.classList.toggle('is-loading', !!on && isActive);
        var loader = b.querySelector('.pp-atc__loader');
        if (loader) loader.hidden = !(on && isActive);
      });
    }

    showAtcError(message) {
      var buy = this.querySelector('.pp-buy');
      if (!buy) return;
      var el = buy.querySelector('[data-pp-atc-error]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'pp-buy__error';
        el.setAttribute('data-pp-atc-error', '');
        el.setAttribute('role', 'alert');
        var row = buy.querySelector('.pp-buy__row');
        if (row && row.parentNode) row.parentNode.insertBefore(el, row.nextSibling);
        else buy.appendChild(el);
      }
      el.textContent = message || 'Sorry, we could not add this to your cart. Please try again.';
      el.hidden = false;
    }

    clearAtcError() {
      var el = this.querySelector('[data-pp-atc-error]');
      if (el) el.hidden = true;
    }

    submitForm() {
      var form = this.querySelector('.pp-config__form');
      if (!form) return;
      // Prefer the remembered submitter so "send later" keeps the Make-another intent.
      var submitter = this._pendingSubmitter || this.querySelector('[data-pp-atc]');
      if (typeof form.requestSubmit === 'function') {
        try {
          form.requestSubmit(submitter || undefined);
        } catch (e) {
          form.requestSubmit();
        }
      } else {
        // Safari < 16 has no requestSubmit; native submit() bypasses our 'submit'
        // listener, so dispatch a submit event to run onSubmit() (props + AJAX add).
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }

    /* --- Photo upload (reuses the store's UploadKit / Uploadcare uploader) --- */

    // Get UploadKit to render .uploadkit into a clickable button: clone the base
    // uploader with a product-matching condition, re-init, then poll for the button.
    ensureUploader() {
      if (this._uploaderReady || this._uploadPoll) return;
      var kit = this.querySelector('.uploadkit');
      if (!kit) return;
      var self = this;
      var tries = 0;
      this._uploadPoll = setInterval(function () {
        tries++;
        var btn = self.querySelector('.uploadkit-button');
        if (btn) {
          clearInterval(self._uploadPoll);
          self._uploadPoll = null;
          self.bindUploader(btn);
          return;
        }
        self.registerUploader();
        if (tries > 60) { // ~18s
          clearInterval(self._uploadPoll);
          self._uploadPoll = null;
        }
      }, 300);
    }

    // Register a product-matching uploader with the UploadKit app (idempotent).
    registerUploader() {
      var UK = window.UploadKit;
      if (!UK || !UK.initialized || typeof UK.init !== 'function') return;
      if (!UK.config || !UK.config.uploaders || !UK.config.uploaders.length) return;
      if (this._uploaderRegistered) return;

      var template = UK.config.uploaders[0];
      var id = (template && template.id ? template.id : 'uploader') + '-pp';
      // Don't double-register if this element re-connected (theme editor).
      var exists = UK.config.uploaders.some(function (u) { return u && u.id === id; });
      if (!exists) {
        var clone;
        try {
          clone = JSON.parse(JSON.stringify(template));
        } catch (e) {
          clone = {};
        }
        clone.id = id;
        clone.name = this.dataset.ppUploadName || 'Pet photo';
        clone.required = false;
        clone.conditions = [{
          field: 'product_title',
          operator: 'contains',
          tags: [],
          value: this.dataset.ppUploadCondition || ''
        }];
        UK.config.uploaders.push(clone);
      }
      this._uploaderRegistered = true;
      try { UK.init(); } catch (err) { /* no-op */ }
    }

    bindUploader(btn) {
      this._uploaderReady = true;
      this.uploadBtn = btn;
      var container = this.querySelector('.uploadkit-attribute-container');
      if (container && window.MutationObserver) {
        var self = this;
        this._uploadObserver = new MutationObserver(function () { self.readUpload(); });
        this._uploadObserver.observe(container, { childList: true, subtree: true, attributes: true });
      }
      this.readUpload();
    }

    openUploader() {
      var btn = this.querySelector('.uploadkit-button');
      if (btn) {
        btn.click();
      } else {
        // Not hydrated yet — kick it off; the user can tap again in a moment.
        this.ensureUploader();
      }
    }

    readUpload() {
      var name = this.uploadBtn && this.uploadBtn.getAttribute('data-uploader-name');
      if (!name) return;
      var direct = this.querySelector('input[name="properties[_' + name + ' (direct link)]"]');
      var url = direct && direct.value;
      if (url) this.showPreview(url);
    }

    showPreview(url) {
      this._hasPhoto = true;
      var prompt = this.querySelector('[data-pp-upload-prompt]');
      var preview = this.querySelector('[data-pp-preview]');
      var img = this.querySelector('[data-pp-preview-img]');
      if (img) img.src = url;
      if (preview) preview.hidden = false;
      if (prompt) prompt.hidden = true;
      var drop = this.querySelector('[data-pp-drop]');
      if (drop) drop.classList.add('has-photo');

      // Once a photo is attached, switch the button to its "uploaded" label
      // (e.g. "Add to cart" instead of "Add to cart, send photo later").
      var atc = this.querySelector('[data-pp-atc]');
      var atcText = this.querySelector('[data-pp-atc-text]');
      if (atc && atcText && atc.dataset.ppAtcAlt) {
        atcText.textContent = atc.dataset.ppAtcAlt;
      }
    }

    /* --- Overlays: Photo Guide modal + upload confirm popup --- */
    openOverlay(el) {
      if (!el) return;
      el.hidden = false;
      // Force reflow so the fade-in transition runs.
      void el.offsetWidth;
      el.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      var closeBtn = el.querySelector('.pp-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    closeOverlay(el) {
      if (!el) return;
      el.classList.remove('is-open');
      // Only unlock page scroll once no overlay remains open.
      if (!this.querySelector('.pp-modal.is-open')) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
      window.setTimeout(function () {
        if (!el.classList.contains('is-open')) el.hidden = true;
      }, 200);
    }

    stepQuantity(delta) {
      var input = this.querySelector('[data-pp-qty-input]');
      if (!input) return;
      var min = parseInt(input.min, 10);
      if (isNaN(min)) min = 1;
      var next = (parseInt(input.value, 10) || min) + delta;
      if (next < min) next = min;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateSleevePrice();
    }

    showStep(scroll) {
      var s = this.step;
      var total = this.total;

      this.panels.forEach(function (p, idx) {
        var on = idx === s;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });

      this.tabs.forEach(function (t, idx) {
        var active = idx === s;
        t.classList.toggle('is-active', active);
        t.classList.toggle('is-complete', idx < s);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.tabIndex = active ? 0 : -1;
      });

      if (this.bar) this.bar.style.width = ((s + 1) / total * 100).toFixed(2) + '%';
      if (this.stepCurrentEl) this.stepCurrentEl.textContent = s + 1;
      if (this.hintEl) {
        var togo = total - (s + 1);
        this.hintEl.textContent =
          togo <= 0
            ? this.hintFinal
            : (togo === 1 ? this.hintTogoOne : this.hintTogoOther).replace('[n]', togo);
      }
      // The final (Upload) step carries its own Back button + buy controls,
      // so hide the shared nav row there; otherwise show Back from step 2 on.
      var onLast = s === total - 1;
      if (this.nav) this.nav.hidden = onLast;
      if (this.backBtn) this.backBtn.hidden = s === 0;
      if (this.nextBtn) this.nextBtn.hidden = onLast;

      // Hydrate the photo uploader once the (now-visible) Upload step is reached.
      if (onLast) this.ensureUploader();

      if (scroll && this.tabs[s] && this.tabs[s].scrollIntoView) {
        this.tabs[s].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }

    findById(id) {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) return this.variants[i];
      }
      return null;
    }

    firstAvailable() {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].a) return this.variants[i];
      }
      return null;
    }

    /* Read the current selection: baseline for options with no UI yet, plus any
       checked radio inside a panel that carries an option position. */
    getSelectedOptions() {
      var selected = this.baseline.slice();
      var panels = this.querySelectorAll('[data-pp-panel][data-pp-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-pp-option-position'), 10) - 1;
        var checked = panel.querySelector('input[type="radio"]:checked');
        if (checked) selected[pos] = checked.value;
      });
      return selected;
    }

    matchExact(sel) {
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        if (v.o.length !== sel.length) continue;
        var ok = true;
        for (var j = 0; j < sel.length; j++) {
          if (v.o[j] !== sel[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    onChange(e) {
      if (!e.target.matches) return;

      // Typing a quantity directly (rather than using the +/- buttons) still has
      // to re-price the sleeve add-on and the running total.
      if (e.target.matches('[data-pp-qty-input]')) {
        this.updateSleevePrice();
        this.recalcTotal();
        return;
      }

      // Un/ticking the sleeve can resolve a pending "enter a name" error, and
      // changes the running total.
      if (e.target.matches('[data-pp-sleeve-toggle]')) {
        var sleevePanel = e.target.closest('[data-pp-panel]');
        if (sleevePanel && !e.target.checked) this.clearError(sleevePanel);
        this.recalcTotal();
        return;
      }

      if (!e.target.matches('input[type="radio"]')) return;
      // Any selection clears a pending validation error on its step.
      var changedPanel = e.target.closest('[data-pp-panel]');
      if (changedPanel && changedPanel.classList.contains('has-error')) {
        this.clearError(changedPanel);
      }
      // Only variant options (inside a panel with an option position) drive
      // variant matching. Custom selections (e.g. placement) are ignored here.
      if (!e.target.closest('[data-pp-option-position]')) return;

      var selected = this.getSelectedOptions();
      var variant = this.matchExact(selected);

      // Relax: if the exact combo doesn't exist (e.g. later options not chosen
      // yet), match on the changed option alone, preferring an available variant.
      if (!variant) {
        var panel = e.target.closest('[data-pp-option-position]');
        if (panel) {
          var pos = parseInt(panel.getAttribute('data-pp-option-position'), 10) - 1;
          var val = e.target.value;
          variant =
            this.variants.filter(function (v) { return v.o[pos] === val && v.a; })[0] ||
            this.variants.filter(function (v) { return v.o[pos] === val; })[0] ||
            null;
        }
      }

      if (variant) this.apply(variant);
    }

    apply(variant) {
      this.current = variant;
      this.baseline = variant.o.slice();
      this.syncOptionRadios(variant);
      this.refreshAvailability();
      this.updateSelectedNames(variant);
      this.updatePrice(variant);
      this.updateGallery(variant);
      this.updateURL(variant);
      this.recalcTotal();

      var idInput = this.section.querySelector && this.section.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;
    }

    // Keep ?variant=<id> in the URL so refresh/share/back restores the selection.
    // replaceState (not pushState) matches Dawn — no stacked history entries.
    updateURL(variant) {
      if (!variant || this.dataset.ppUpdateUrl === 'false') return;
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        /* no-op — URL syncing is a progressive enhancement */
      }
    }

    // Grey out + disable any option value that can't reach an available variant
    // given the OTHER panels' selections. The checked value always stays enabled.
    refreshAvailability() {
      if (!this.variants || !this.variants.length) return;
      var self = this;
      var selected = this.getSelectedOptions();
      var panels = this.querySelectorAll('[data-pp-panel][data-pp-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-pp-option-position'), 10) - 1;
        var radios = panel.querySelectorAll('input[type="radio"]');
        Array.prototype.forEach.call(radios, function (input) {
          var label = input.closest('[data-value]');
          var val = input.value;
          var reachable = self.variants.some(function (v) {
            if (!v.a || v.o[pos] !== val) return false;
            for (var k = 0; k < selected.length; k++) {
              if (k === pos || selected[k] == null) continue;
              if (v.o[k] !== selected[k]) return false;
            }
            return true;
          });
          if (label) label.classList.toggle('is-unavailable', !reachable);
          input.disabled = !reachable && !input.checked;
        });
      });
    }

    // Re-check every option radio to match the applied variant, so a fallback
    // match in onChange can't leave the picker out of sync with [name="id"].
    syncOptionRadios(variant) {
      var panels = this.querySelectorAll('[data-pp-panel][data-pp-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-pp-option-position'), 10) - 1;
        var want = variant.o[pos];
        var radios = panel.querySelectorAll('input[type="radio"]');
        Array.prototype.forEach.call(radios, function (radio) {
          radio.checked = radio.value === want;
        });
      });
    }

    updateSelectedNames(variant) {
      var panels = this.querySelectorAll('[data-pp-panel][data-pp-option-position]');
      Array.prototype.forEach.call(panels, function (panel) {
        var pos = parseInt(panel.getAttribute('data-pp-option-position'), 10) - 1;
        var sel = panel.querySelector('[data-pp-selected]');
        if (sel && variant.o[pos] != null) sel.textContent = variant.o[pos];
      });
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    updatePrice(variant) {
      var now = this.section.querySelector('[data-pp-price]');
      var was = this.section.querySelector('[data-pp-compare]');
      if (now) now.textContent = this.money(variant.p);
      if (was) {
        if (variant.c && variant.c > variant.p) {
          was.textContent = this.money(variant.c);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }
    }

    updateGallery(variant) {
      if (!variant.m) return;
      var gallery = this.section.querySelector('pet-portrait-gallery');
      if (gallery && typeof gallery.selectMedia === 'function') {
        gallery.selectMedia(variant.m);
      }
    }
  }

  if (!window.customElements.get('pet-portrait-configurator')) {
    window.customElements.define('pet-portrait-configurator', PetPortraitConfigurator);
  }

  /* ------------------------------------------------------------------------
     <pet-portrait-sticky> — CTA bar shown once the configurator scrolls out of
     view; scrolls back on click. IntersectionObserver, so no-support just hides it.
     ---------------------------------------------------------------------- */
  class PetPortraitSticky extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.pp-pdp') || document;
      this.target = this.section.querySelector('pet-portrait-configurator');
      this.btn = this.querySelector('[data-pp-sticky-btn]');
      if (!this.target || !this.btn || !window.IntersectionObserver) return;

      this._onClick = this.scrollToTarget.bind(this);
      this.btn.addEventListener('click', this._onClick);

      this.observer = new window.IntersectionObserver(
        function (entries) {
          this.hidden = entries[0].isIntersecting;
        }.bind(this),
        { threshold: 0 }
      );
      this.observer.observe(this.target);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.btn && this._onClick) {
        this.btn.removeEventListener('click', this._onClick);
      }
    }

    scrollToTarget() {
      if (!this.target) return;
      // Offset comes from scroll-margin-top on the configurator, so the gap
      // left for the theme header is tunable in CSS rather than hard-coded here.
      this.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (!window.customElements.get('pet-portrait-sticky')) {
    window.customElements.define('pet-portrait-sticky', PetPortraitSticky);
  }

  /* ------------------------------------------------------------------------
     Offer pill → linked section. A delegated listener glides the price-block
     anchor to the target's scroll-margin-top; a bare/stale hash is swallowed.
     ---------------------------------------------------------------------- */
  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-pp-offer]');
    if (!link) return;

    // Off-page links (http…, /pages/…, mailto:) are none of our business.
    var href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;

    // A bare "#" is the unconfigured default. Nothing to scroll to, and the
    // browser's fallback is to yank the page to the top — so just eat it.
    if (href.length < 2) {
      e.preventDefault();
      return;
    }

    var target;
    try {
      target = document.querySelector(href);
    } catch (err) {
      return; // Not a valid selector — let the browser deal with it.
    }

    // Stale anchor: the section it pointed at is gone. Same story as "#".
    if (!target) {
      e.preventDefault();
      return;
    }
    if (!target.scrollIntoView) return;

    e.preventDefault();
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start'
    });

    // Keep the address bar and the back button in step with a real anchor jump,
    // without the extra scroll that setting location.hash would trigger.
    if (window.history && window.history.replaceState) {
      try {
        window.history.replaceState(null, '', href);
      } catch (err) {
        /* no-op */
      }
    }
  });
})();
